import { useSyncExternalStore } from 'react';
import { CheckCircle2, Loader2, Share2, TriangleAlert, X } from 'lucide-react';
import {
  dismissBackgroundPublish,
  getBackgroundPublishes,
  retryBackgroundPublish,
  subscribeBackgroundPublish,
} from '@/lib/challenges/background-publish';
import { shareContent } from '@/lib/native-share';

/**
 * Message dans l'app pour les défis publiés en arrière-plan (`background-publish.ts`) : « en cours »,
 * puis « publié » (partager), ou « pas publié » (réessayer). Monté une fois à la racine, en haut de
 * l'écran (la barre du bas reste libre).
 */
export function ChallengePublishToast() {
  const jobs = useSyncExternalStore(
    subscribeBackgroundPublish,
    getBackgroundPublishes,
    getBackgroundPublishes,
  );
  if (jobs.length === 0) return null;
  return (
    <div className="pointer-events-none fixed inset-x-0 top-0 z-[90] flex flex-col items-center gap-2 px-4 pt-[max(0.75rem,env(safe-area-inset-top))]">
      {jobs.map((job) => (
        <div
          key={job.id}
          role="status"
          aria-live="polite"
          data-testid="challenge-publish-toast"
          className="ocean:bg-[#0b1f4a] ocean:text-white pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-2xl bg-white px-4 py-3 text-sm shadow-lg ring-1 ring-black/5"
        >
          {job.status === 'publishing' && (
            <>
              <Loader2 className="text-primary-500 h-5 w-5 shrink-0 animate-spin" aria-hidden />
              <p className="text-ink-900 min-w-0 flex-1">
                <span className="font-semibold">Publication de ton défi…</span>
                <span className="text-ink-500 block text-xs">
                  Tu peux continuer, on te prévient quand c’est fait.
                </span>
              </p>
            </>
          )}
          {job.status === 'published' && (
            <>
              <CheckCircle2 className="text-xp-500 h-5 w-5 shrink-0" aria-hidden />
              <p className="text-ink-900 min-w-0 flex-1">
                <span className="font-semibold">Ton défi est publié !</span>
                <span className="text-ink-500 block truncate text-xs">
                  « {job.title} » est dans ton Feed.
                </span>
              </p>
              <button
                type="button"
                className="text-primary-600 inline-flex min-h-11 min-w-11 items-center justify-center rounded-full"
                aria-label="Partager le défi"
                onClick={() =>
                  void shareContent({
                    title: job.title,
                    text: 'Relève mon défi Jeraisonne !',
                    url: job.url,
                  })
                }
              >
                <Share2 className="h-5 w-5" aria-hidden />
              </button>
            </>
          )}
          {job.status === 'failed' && (
            <>
              <TriangleAlert className="h-5 w-5 shrink-0 text-red-500" aria-hidden />
              <p className="text-ink-900 min-w-0 flex-1">
                <span className="font-semibold">Ton défi n’a pas été publié</span>
                <span className="text-ink-500 block text-xs">{job.error}</span>
              </p>
              <button
                type="button"
                className="font-display text-primary-600 min-h-11 shrink-0 rounded-full px-2 text-xs font-bold"
                onClick={() => retryBackgroundPublish(job.id)}
              >
                Réessayer
              </button>
            </>
          )}
          {job.status !== 'publishing' && (
            <button
              type="button"
              className="text-ink-400 -mr-2 inline-flex min-h-11 min-w-11 items-center justify-center rounded-full"
              aria-label="Fermer"
              onClick={() => dismissBackgroundPublish(job.id)}
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
